import React, { memo, useState } from 'react';
import { Dropdown, Avatar, Space, Typography, Tooltip } from 'antd';
import { UserOutlined, LogoutOutlined, TrophyOutlined, QuestionCircleOutlined, RobotOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';

export interface UserMenuProps {
  userName: string;
  userEmail?: string;
  userRole?: string;
  onLogout: () => void;
  onEditProfile: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = memo(({
  userName,
  userRole,
  onLogout,
  onEditProfile,
}) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const isParticipant = userRole === 'PARTICIPANT';
  const isAdministrator = userRole === 'ADMINISTRATOR';

  const items: MenuProps['items'] = [
    {
      key: 'profile',
      label: 'Meu Perfil',
      icon: <UserOutlined />,
      onClick: onEditProfile,
    },
    ...(isParticipant ? [
      {
        key: 'engagement',
        label: 'Pontuação de Engajamento',
        icon: <TrophyOutlined />,
        onClick: () => navigate('/engagement'),
      },
    ] : []),
    ...(isAdministrator ? [{ key: 'ai-models', label: 'Modelos de IA', icon: <RobotOutlined />, onClick: () => navigate('/admin/ai-models') }] : []),
    {
      key: 'faq',
      label: 'Perguntas frequentes',
      icon: <QuestionCircleOutlined />,
      onClick: () => navigate('/faq'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      label: 'Sair',
      icon: <LogoutOutlined />,
      onClick: onLogout,
      danger: true,
    },
  ];

  const displayName = userName || 'Usuário';
  const truncatedName = displayName.length > 20 
    ? `${displayName.substring(0, 20)}...` 
    : displayName;
  
  const showTooltip = displayName.length > 20;

  return (
    <Dropdown 
      menu={{ items }} 
      trigger={['click']} 
      placement="bottomRight"
      onOpenChange={setDropdownOpen}
    >
      <Space 
        style={{ cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        aria-label="Menu de usuário"
        aria-haspopup="true"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.currentTarget.click();
          }
        }}
      >
        <Tooltip 
          title={showTooltip ? displayName : ''} 
          placement="bottom"
          open={!dropdownOpen ? undefined : false}
        >
          <Avatar icon={<UserOutlined />} />
        </Tooltip>
        <Typography.Text>{truncatedName}</Typography.Text>
      </Space>
    </Dropdown>
  );
});
